<template>
  <transition name="intake">
    <div v-if="visible" class="intake" :class="'intake--' + toneName" role="alert" aria-live="assertive">
      <div class="intake__row">
        <span class="intake__pulse" aria-hidden="true"><i></i></span>
        <div class="intake__main">
          <div class="intake__title">
            {{ state.title }}
            <span v-if="since" class="intake__since">{{ since }}</span>
          </div>
          <div class="intake__text">{{ state.text }}</div>
        </div>
        <div class="intake__actions">
          <button type="button" class="intake__btn intake__btn--gold" @click="open = !open">
            {{ open ? 'Свернуть' : 'Подробнее' }}
            <span class="intake__chev" :class="{ 'is-open': open }" aria-hidden="true">▾</span>
          </button>
          <button type="button" class="intake__btn" title="Скрыть на 30 минут" @click="hide">Скрыть</button>
        </div>
      </div>

      <div class="intake__explain">
        <p><b>Что это значит:</b> {{ meaningText }}</p>
        <p><b>Что делать:</b> {{ todoText }}</p>
        <div v-if="stuck.length" class="intake__chips">
          <span class="intake__chips-title">Рейсы:</span>
          <span v-for="item in stuck.slice(0, 6)" :key="item.number" class="intake__chip" :title="item.reason">
            {{ item.number }}
          </span>
          <span v-if="stuck.length > 6" class="intake__chip intake__chip--more">+{{ stuck.length - 6 }}</span>
        </div>
        <div class="intake__legend">
          <span v-for="item in legend" :key="item.tone" :class="['intake__legend-item', 'is-' + item.tone]">
            <i aria-hidden="true"></i><b>{{ item.label }}</b> — {{ item.text }}
          </span>
          <span class="intake__legend-item is-calm">Плашка исчезнет сама, когда всё восстановится. {{ reassure }}</span>
        </div>
      </div>

      <transition name="intake-more">
        <div v-if="open" class="intake__more">
          <p v-if="state.action" class="intake__action">{{ state.action }}</p>
          <ul v-if="stuck.length" class="intake__list">
            <li v-for="item in stuck" :key="item.number">{{ line(item) }}</li>
          </ul>
          <router-link :to="{ path: '/knowledge', query: { a: 'tms-intake' } }" class="intake__kb">
            Как это работает и что делать — в Базе знаний →
          </router-link>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
// Плашка сверху всех экранов ERP: рейсы из TMS не поступают / часть рейсов не загружается.
// Решение Анны 02.11.2026: пояснение — прямо в плашке (что значит цвет, что делать, какие рейсы).
import { fetchTmsIntake } from '@/backend/workspaceApi.js'
import {
  POLL_MS, HIDE_KEY, LEGEND, REASSURE, shouldShow, tone, sinceText, stuckLine, isHidden, hideRecord, meaning, todo,
} from '@/components/system/intakeBanner.mjs'

function readHidden() {
  try {
    return window.localStorage.getItem(HIDE_KEY)
  } catch (e) {
    return null
  }
}

export default {
  name: 'TmsIntakeBanner',
  data() {
    return {
      state: null, open: false, hiddenRecord: readHidden(), now: Date.now(), timer: null, first: null,
      legend: LEGEND, reassure: REASSURE,
    }
  },
  computed: {
    visible() {
      return shouldShow(this.state) && !isHidden(this.hiddenRecord, this.state, this.now)
    },
    toneName() {
      return tone(this.state)
    },
    since() {
      return this.state ? sinceText(this.state.since, new Date(this.now)) : ''
    },
    stuck() {
      return (this.state && Array.isArray(this.state.stuck)) ? this.state.stuck.slice(0, 20) : []
    },
    meaningText() {
      return meaning(this.state)
    },
    todoText() {
      return todo(this.state)
    },
  },
  mounted() {
    this.first = setTimeout(this.load, 3000)
    this.timer = setInterval(this.tick, POLL_MS)
    document.addEventListener('visibilitychange', this.onVisible)
  },
  beforeUnmount() {
    clearTimeout(this.first)
    clearInterval(this.timer)
    document.removeEventListener('visibilitychange', this.onVisible)
  },
  methods: {
    line: stuckLine,
    async load() {
      try {
        this.state = await fetchTmsIntake()
      } catch (e) {
        // Плашка — подсказка, а не экран: при сбое запроса просто ждём следующую проверку.
      }
      this.now = Date.now()
    },
    tick() {
      if (document.visibilityState === 'hidden') return
      this.load()
    },
    onVisible() {
      if (document.visibilityState === 'visible') this.load()
    },
    hide() {
      this.hiddenRecord = hideRecord(this.state)
      this.open = false
      try {
        window.localStorage.setItem(HIDE_KEY, this.hiddenRecord)
      } catch (e) {
        /* без localStorage скрываем до перезагрузки страницы */
      }
    },
  },
}
</script>

<style scoped>
.intake {
  --accent: #ff4d4f;
  --accent-soft: rgba(255, 77, 79, .18);
  position: relative;
  flex-shrink: 0;
  color: #eef0f4;
  background:
    radial-gradient(120% 220% at 0% 0%, var(--accent-soft), transparent 55%),
    linear-gradient(100deg, #1b1f29 0%, #262b37 60%, #1f2430 100%);
  border-bottom: 1px solid rgba(242, 183, 5, .35);
  box-shadow: 0 6px 18px rgba(15, 18, 26, .25);
  overflow: hidden;
  z-index: 20;
}
.intake--amber {
  --accent: #f2a705;
  --accent-soft: rgba(242, 167, 5, .2);
}
.intake::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, transparent, var(--accent), #f2d27a, transparent);
  background-size: 200% 100%;
  animation: intake-sweep 3.2s linear infinite;
}
.intake__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 20px 6px;
}
.intake__pulse {
  position: relative;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 12px var(--accent);
}
.intake__pulse i {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid var(--accent);
  animation: intake-ring 1.8s ease-out infinite;
}
.intake__main {
  flex: 1;
  min-width: 0;
}
.intake__title {
  font-weight: 700;
  font-size: 14px;
  letter-spacing: .01em;
}
.intake__since {
  margin-left: 8px;
  font-weight: 500;
  color: #f2d27a;
}
.intake__text {
  margin-top: 2px;
  font-size: 13px;
  color: #c5cad6;
  line-height: 1.35;
}
.intake__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.intake__btn {
  border: 1px solid rgba(255, 255, 255, .18);
  background: rgba(255, 255, 255, .06);
  color: #e6e7ea;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background .2s, transform .2s, border-color .2s;
}
.intake__btn:hover {
  background: rgba(255, 255, 255, .12);
  transform: translateY(-1px);
}
.intake__btn--gold {
  color: #1f2430;
  border-color: transparent;
  background: linear-gradient(135deg, #f0d27c, #d9b65a 60%, #b8923a);
}
.intake__btn--gold:hover {
  background: linear-gradient(135deg, #f7df98, #e2c06a 60%, #c39c44);
}
.intake__chev {
  display: inline-block;
  margin-left: 4px;
  transition: transform .25s;
}
.intake__chev.is-open {
  transform: rotate(180deg);
}
.intake__explain {
  padding: 0 20px 10px 48px;
  font-size: 13px;
  line-height: 1.4;
  color: #d5d9e2;
  display: grid;
  gap: 4px;
}
.intake__explain b {
  color: #f2d27a;
  font-weight: 600;
}
.intake__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}
.intake__chips-title {
  color: #9aa0ad;
}
.intake__chip {
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid var(--accent);
  background: rgba(255, 255, 255, .06);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  cursor: help;
}
.intake__chip--more {
  border-style: dashed;
  cursor: default;
}
.intake__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-top: 4px;
  font-size: 12px;
  color: #9aa0ad;
}
.intake__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.intake__legend-item b {
  color: #e6e7ea;
}
.intake__legend-item i {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
.intake__legend-item.is-red i {
  background: #ff4d4f;
}
.intake__legend-item.is-amber i {
  background: #f2a705;
}
.intake__more {
  padding: 0 20px 12px 48px;
  font-size: 13px;
  color: #d5d9e2;
}
.intake__action {
  margin-bottom: 6px;
}
.intake__list {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  display: grid;
  gap: 4px;
}
.intake__list li {
  padding: 5px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, .05);
  border-left: 3px solid var(--accent);
  font-variant-numeric: tabular-nums;
}
.intake__kb {
  color: #f2d27a;
  text-decoration: none;
  font-weight: 600;
}
.intake__kb:hover {
  text-decoration: underline;
}

.intake-enter-active,
.intake-leave-active {
  transition: transform .45s cubic-bezier(.2, .9, .3, 1.2), opacity .35s ease;
}
.intake-enter-from,
.intake-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
.intake-more-enter-active,
.intake-more-leave-active {
  transition: opacity .25s ease, transform .25s ease;
}
.intake-more-enter-from,
.intake-more-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@keyframes intake-ring {
  0% { transform: scale(.6); opacity: .9; }
  100% { transform: scale(1.6); opacity: 0; }
}
@keyframes intake-sweep {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
@media (max-width: 640px) {
  .intake__row { flex-wrap: wrap; padding: 10px 14px 6px; }
  .intake__actions { width: 100%; justify-content: flex-end; }
  .intake__explain, .intake__more { padding-left: 14px; padding-right: 14px; }
}
@media (prefers-reduced-motion: reduce) {
  .intake *, .intake, .intake::after { animation: none !important; transition: none !important; }
}
</style>
